import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Checkbox,
  useDisclosure,
  Alert,
  AlertIcon,
  FormHelperText,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiShield, FiUsers } from 'react-icons/fi';

const API_BASE = '/api/v1';

function SuperAdmin() {
  const toast = useToast();
  
  // Global Settings State
  const [settings, setSettings] = useState({ ip_whitelist: '', ip_denylist: '' });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Admin Users State
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', is_superuser: false });
  
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  // Fetch Global Settings
  useEffect(() => {
    fetch(`${API_BASE}/global-settings/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setSettings(data[0]);
        } else if (data && !Array.isArray(data)) {
          setSettings(data);
        }
        setLoadingSettings(false);
      })
      .catch(() => setLoadingSettings(false));
  }, []);

  // Fetch Admin Users
  useEffect(() => {
    fetch(`${API_BASE}/admin-users/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setUsers(Array.isArray(data) ? data : []);
        setLoadingUsers(false);
      })
      .catch(() => setLoadingUsers(false));
    
    // Get current user info
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE}/admin-users/me/`, { headers: getAuthHeaders() })
        .then(res => res.json())
        .then(data => setCurrentUser(data))
        .catch(() => {});
    }
  }, []);

  // Save Global Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/1/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        toast({ title: 'Settings saved', status: 'success', duration: 3000 });
      } else {
        throw new Error('Failed to save');
      }
    } catch (e) {
      toast({ title: 'Error saving settings', status: 'error', duration: 3000 });
    }
    setSavingSettings(false);
  };

  // User Modal Handlers
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ username: '', email: '', password: '', is_superuser: false });
    onUserModalOpen();
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ 
      username: user.username, 
      email: user.email || '', 
      password: '', 
      is_superuser: user.is_superuser,
      is_active: user.is_active
    });
    onUserModalOpen();
  };

  const handleSaveUser = async () => {
    const method = editingUser ? 'PUT' : 'POST';
    const url = editingUser ? `${API_BASE}/admin-users/${editingUser.id}/` : `${API_BASE}/admin-users/`;
    
    const payload = { ...userForm };
    if (!payload.password) delete payload.password;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        if (editingUser) {
          setUsers(users.map(u => u.id === editingUser.id ? data : u));
        } else {
          setUsers([...users, data]);
        }
        toast({ title: editingUser ? 'User updated' : 'User created', status: 'success', duration: 3000 });
        onUserModalClose();
      } else {
        toast({ title: data.error || 'Error saving user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving user', status: 'error', duration: 3000 });
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteModalOpen();
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/admin-users/${userToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok || data.deleted) {
        setUsers(users.filter(u => u.id !== userToDelete.id));
        toast({ title: 'User deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Error deleting user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting user', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setUserToDelete(null);
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>Super Admin</Heading>
      
      <Tabs colorScheme="orange" variant="enclosed">
        <TabList>
          <Tab><FiShield style={{ marginRight: 8 }} /> Global Settings</Tab>
          <Tab><FiUsers style={{ marginRight: 8 }} /> Admin Users</Tab>
        </TabList>

        <TabPanels>
          {/* Global Settings Tab */}
          <TabPanel>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
              <VStack spacing={6} align="stretch">
                <Heading size="md">IP Access Control</Heading>
                
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  Configure which IP addresses can access the admin UI. Leave whitelist blank to allow all IPs.
                </Alert>

                <FormControl>
                  <FormLabel fontWeight="bold">IP Whitelist (Allowlist)</FormLabel>
                  <Textarea
                    value={settings.ip_whitelist}
                    onChange={(e) => setSettings({ ...settings, ip_whitelist: e.target.value })}
                    placeholder="Enter allowed IP addresses, comma-separated (e.g., 192.168.1.1, 10.0.0.0/24)"
                    rows={4}
                    fontFamily="mono"
                  />
                  <FormHelperText>
                    Leave blank to allow all IPs. Supports individual IPs and CIDR notation.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold">IP Denylist (Blocklist)</FormLabel>
                  <Textarea
                    value={settings.ip_denylist}
                    onChange={(e) => setSettings({ ...settings, ip_denylist: e.target.value })}
                    placeholder="Enter blocked IP addresses, comma-separated (e.g., 1.2.3.4, 5.6.7.8)"
                    rows={4}
                    fontFamily="mono"
                  />
                  <FormHelperText>
                    Denylist takes precedence over whitelist. These IPs will always be blocked.
                  </FormHelperText>
                </FormControl>

                <HStack>
                  <Button
                    colorScheme="orange"
                    onClick={handleSaveSettings}
                    isLoading={savingSettings}
                  >
                    Save Settings
                  </Button>
                </HStack>

                {settings.updated_at && (
                  <Text fontSize="sm" color="gray.500">
                    Last updated: {new Date(settings.updated_at).toLocaleString()}
                  </Text>
                )}
              </VStack>
            </Box>
          </TabPanel>

          {/* Admin Users Tab */}
          <TabPanel>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
              <HStack justify="space-between" mb={6}>
                <Heading size="md">Admin Users</Heading>
                <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddUser}>
                  Add User
                </Button>
              </HStack>

              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Username</Th>
                    <Th>Email</Th>
                    <Th>Status</Th>
                    <Th>Role</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map(user => (
                    <Tr key={user.id}>
                      <Td fontWeight="medium">{user.username}</Td>
                      <Td>{user.email || '-'}</Td>
                      <Td>
                        <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={user.is_superuser ? 'orange' : 'gray'}>
                          {user.is_superuser ? 'Super Admin' : 'Admin'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<FiEdit2 />}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditUser(user)}
                            aria-label="Edit"
                          />
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => confirmDeleteUser(user)}
                            aria-label="Delete"
                            isDisabled={currentUser?.id === user.id}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {users.length === 0 && (
                <Text color="gray.500" textAlign="center" py={8}>No users found</Text>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingUser ? 'Edit User' : 'Add User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Username</FormLabel>
              <Input
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                placeholder="Username"
                isDisabled={!!editingUser}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="user@example.com"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</FormLabel>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="Password"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <Checkbox
                isChecked={userForm.is_superuser}
                onChange={(e) => setUserForm({ ...userForm, is_superuser: e.target.checked })}
                colorScheme="orange"
              >
                Super Admin
              </Checkbox>
            </FormControl>
            {editingUser && (
              <FormControl display="flex" alignItems="center">
                <Checkbox
                  isChecked={userForm.is_active}
                  onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                  colorScheme="green"
                >
                  Active
                </Checkbox>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUserModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveUser}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete user "{userToDelete?.username}"?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteUser}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SuperAdmin;
